import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { img, sameOriginImg } from '../../lib/bungie';
import { buildScene, type Scene } from '../gear3d';
import { godRolls } from '../godrolls';
import {
  AMMO, CATEGORIES, CLASS_NAMES, DAMAGE_COLORS, ELEMENTS, category, categoryImage, d2Index, findActivity, guide, itemByHash, loadItem, manifestVersion, norm,
  type D2Index, type D2Item, type GodRoll, type Guide, type ItemDetail, type Perk, type PerkColumn,
} from '../d2info';
import { Art, Asking, Bar, Chip, Draw, Pic, SmallButton } from '../ui';
import MetaPages from './MetaPages';
import '../d2info.css';

/**
 * D2 Info (port of the app's D2InfoPage): activities with their loot, every weapon and armour piece searchable,
 * an item's perks with the community god rolls, raid and dungeon guides from the Destiny Wiki, and the meta.
 */

type View = { name: 'activities' | 'pvp' | 'pve' | 'builds' | 'search' } | { name: 'activity'; source: string } | { name: 'item'; item: D2Item } | { name: 'guide'; title: string };
const SECTIONS = [['activities', 'Activities'], ['pvp', 'PvP Meta'], ['pve', 'PvE Meta'], ['builds', 'Meta Builds'], ['search', 'Item Search']] as const;

export default function D2InfoTab() {
  // ?item=<name or hash>: open that weapon (the Raid Planner's loot links here)
  const [params] = useSearchParams();
  const openItem = params.get('item') ?? '';
  // ?section=search: start on that section (the Operations page's D2 Info card opens Item Search)
  const first = SECTIONS.find(([k]) => k === params.get('section'))?.[0] ?? 'activities';
  const [ix, setIx] = useState<D2Index>();
  const [error, setError] = useState('');
  const [stack, setStack] = useState<View[]>([{ name: first }]);
  const view = stack[stack.length - 1];
  const push = (v: View) => { setStack((s) => [...s, v]); window.scrollTo({ top: 0 }); };
  const pop = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  const go = (v: View) => setStack([v]);

  const [version, setVersion] = useState('');
  useEffect(() => { d2Index().then(setIx).catch((e) => setError(String(e?.message ?? e))); manifestVersion().then(setVersion).catch(() => undefined); }, []);
  useEffect(() => {
    if (!ix || !openItem) return;
    let live = true;
    // a number: that exact item, whatever it is (itemByHash); a name (the Raid Planner's loot): the best match in the index
    const byName = () => { const n = norm(openItem); return ix.items.filter((i) => i.norm === n).sort((a, b) => Number(b.itemType === 3) - Number(a.itemType === 3) || b.hash - a.hash)[0]; };
    (/^\d+$/.test(openItem) ? itemByHash(ix, Number(openItem)) : Promise.resolve(byName()))
      .then((it) => { if (live && it) go({ name: 'item', item: it }); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [ix, openItem]);

  if (error) return <Asking text={`Bungie's item database could not be read: ${error}`} />;
  if (!ix) return <Asking text="Reading Bungie's item database (the first time it is downloaded once per Destiny update)..." />;
  const ctx = { ix, push };
  return (
    <div className="d-page">
      <div className="d-topbar">
        <div className="g-chips">
          {SECTIONS.map(([k, label]) => <Chip key={k} on={stack.length === 1 && view.name === k} onClick={() => go({ name: k })}>{label}</Chip>)}
        </div>
        {/* as the app's top right: how many items it knows and from which Bungie database */}
        <span className="d-dbinfo">{ix.items.filter((i) => i.itemType === 2 || i.itemType === 3).length.toLocaleString()} weapons and armour pieces{version ? `   |   Bungie database ${version}` : ''}</span>
      </div>
      {stack.length > 1 && (
        <div className="d-crumb">
          <SmallButton onClick={pop}>Back</SmallButton>
          <span>{crumb(view)}</span>
        </div>
      )}
      {view.name === 'activities' && <Activities {...ctx} />}
      {view.name === 'search' && <Search {...ctx} />}
      {view.name === 'activity' && <ActivityPage {...ctx} source={view.source} />}
      {view.name === 'item' && <ItemPage key={view.item.hash} {...ctx} it={view.item} />}
      {view.name === 'guide' && <GuidePage title={view.title} />}
      {(view.name === 'pvp' || view.name === 'pve' || view.name === 'builds') && <MetaPages {...ctx} side={view.name} openItem={(it) => push({ name: 'item', item: it })} />}
    </div>
  );
}

function crumb(v: View) {
  if (v.name === 'activity') return `Activities  /  ${category(v.source)}  /  ${v.source}`;
  if (v.name === 'item') return `Item  /  ${v.item.name}`;
  if (v.name === 'guide') return `Activities  /  Guide  /  ${v.title}`;
  return '';
}

type Ctx = { ix: D2Index; push: (v: View) => void };

// ---------------- pieces ----------------

const TIER_BG: Record<number, string> = { 6: '#CEAE33', 5: '#7A4FB0', 4: '#5076A3' };

/** An item tile; pve / pvp: it has a community god roll for that mode (Item Search marks them). */
export function ItemCard({ it, onClick, pve, pvp }: { it: D2Item; onClick: () => void; pve?: boolean; pvp?: boolean }) {
  const sub = [it.type];
  if (it.itemType === 3 && ELEMENTS[it.damage]) sub.push(ELEMENTS[it.damage]);
  if (it.itemType === 2 && CLASS_NAMES[it.classType]) sub.push(CLASS_NAMES[it.classType]);
  return (
    <button type="button" className="d-item" onClick={onClick} title={it.source ? `${it.name}\n${it.source}` : it.name}>
      <span className="ic" style={{ background: TIER_BG[it.tier] ?? '#4A5363' }}>
        <Pic src={it.icon} lazy />
        {it.mark && <Pic src={it.mark} lazy />}
      </span>
      <span className="words">
        <b><span className="nm">{it.name}</span>{(pve || pvp) && <span className="d-modes">{pve && <i className="pve">PvE</i>}{pvp && <i className="pvp">PvP</i>}</span>}</b>
        <span style={{ color: it.itemType === 3 ? DAMAGE_COLORS[it.damage] : undefined }}>{sub.join('  |  ')}</span>
      </span>
    </button>
  );
}

function Tiles({ list, push, max = 150 }: { list: D2Item[]; push: Ctx['push']; max?: number }) {
  return (
    <>
      <div className="d-tiles">{list.slice(0, max).map((it) => <ItemCard key={it.hash} it={it} onClick={() => push({ name: 'item', item: it })} />)}</div>
      {list.length > max && <p className="d-note">Showing the first {max} of {list.length}. Use Item Search to narrow it down.</p>}
    </>
  );
}

function ArtTile({ title, sub, image, tall, on, onClick, tip }: { title: string; sub: string; image?: string; tall?: boolean; on?: boolean; onClick: () => void; tip?: string }) {
  return (
    <button type="button" className={`d-art ${tall ? 'tall' : ''} ${on ? 'on' : ''}`} onClick={onClick} title={tip}
      style={image ? { backgroundImage: `url(${img(image)})` } : undefined}>
      <span className="fade">
        <b>{title}</b>
        {sub && <span>{sub}</span>}
      </span>
    </button>
  );
}

/** A perk; pve / pvp: the community picks it for that mode (shown as a tag, the chip lit in that mode's colour). */
function PerkChip({ p, pve, pvp }: { p: Perk; pve?: boolean; pvp?: boolean }) {
  const modes = [pve && 'PvE', pvp && 'PvP'].filter(Boolean).join(' and ');
  return (
    <div className={`d-perk ${pve ? 'pve' : ''} ${pvp ? 'pvp' : ''}`} title={`${p.name}\n${p.type}${modes ? `\nIn the community's ${modes} god rolls` : ''}${p.desc ? `\n\n${p.desc}` : ''}`}>
      {p.icon && <Pic src={p.icon} width={26} height={26} />}
      <span className="n">{p.name}</span>
      {(pve || pvp) && <span className="d-modes">{pve && <i className="pve">PvE</i>}{pvp && <i className="pvp">PvP</i>}</span>}
    </div>
  );
}

const Head = ({ children }: { children: string }) => <div className="d-head">{children}</div>;

// ---------------- activities ----------------

function Activities({ ix, push }: Ctx) {
  const [open, setOpen] = useState<string>();
  const withSource = useMemo(() => ix.items.filter((i) => i.source), [ix]);
  return (
    <>
      <Head>Activities</Head>
      <div className="d-cats">
        {CATEGORIES.map(([cat]) => {
          const inCat = withSource.filter((i) => i.category === cat);
          return <ArtTile key={cat} tall title={cat} sub={`${new Set(inCat.map((i) => i.source)).size} activities  |  ${inCat.length} items`} image={categoryImage(ix, cat)}
            on={open === cat} onClick={() => setOpen(open === cat ? undefined : cat)} />;
        })}
      </div>
      {open && (
        <>
          <Head>{open}</Head>
          <div className="d-acts">
            {[...groupBy(withSource.filter((i) => i.category === open), (i) => i.source)].sort(([a], [b]) => a.localeCompare(b)).map(([src, list]) => {
              const act = findActivity(ix, src).find((a) => a.image);
              return <ArtTile key={src} title={src.length > 60 ? src.slice(0, 57) + '...' : src} tip={src} sub={`${list.length} items${act?.type ? `  |  ${act.type}` : ''}`} image={act?.image}
                onClick={() => push({ name: 'activity', source: src })} />;
            })}
          </div>
        </>
      )}
      <p className="d-note">Pictures and details from Bungie's activity data; loot pools from the "Source" line of every item (the same line you see in your Collections). Bungie does not say which encounter drops what, so pools are per activity.</p>
    </>
  );
}

function groupBy<T>(list: T[], key: (t: T) => string) {
  const m = new Map<string, T[]>();
  for (const x of list) { const k = key(x); const g = m.get(k); if (g) g.push(x); else m.set(k, [x]); }
  return m;
}

function LootGroups({ list, push }: { list: D2Item[]; push: Ctx['push'] }) {
  const groups: [string, (i: D2Item) => boolean][] = [['Exotics', (i) => i.tier === 6], ['Weapons', (i) => i.itemType === 3 && i.tier !== 6], ['Armour', (i) => i.itemType === 2 && i.tier !== 6]];
  return (
    <>
      {groups.map(([title, pick]) => {
        const sel = list.filter(pick).sort((a, b) => a.classType - b.classType || a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
        return sel.length ? <div key={title}><Head>{title}</Head><Tiles list={sel} push={push} /></div> : null;
      })}
    </>
  );
}

function ActivityPage({ ix, push, source }: Ctx & { source: string }) {
  const acts = findActivity(ix, source);
  const main = acts.filter((a) => a.desc).sort((a, b) => a.light - b.light)[0];
  const place = main?.destination ? main.destination + (main.place && main.place !== main.destination ? ' - ' + main.place : '') : '';
  const variants = [...groupBy(acts.filter((a) => a.light > 0), (a) => String(a.light)).keys()].map(Number).sort((a, b) => a - b);
  const raid = main && /raid|dungeon/i.test(`${main.type} ${source}`);
  return (
    <>
      <div className="d-hero" style={main?.image ? { backgroundImage: `linear-gradient(90deg, rgba(16,12,34,0.9), rgba(16,12,34,0.35)), url(${img(main.image)})` } : undefined}>
        <h2>{(main?.name ?? source).toUpperCase()}</h2>
        {main && <div className="gold">{[main.type, place].filter(Boolean).join('   |   ')}</div>}
        {main && <p>{main.desc}</p>}
      </div>
      {main ? (
        <>
          <Head>How to do it</Head>
          <dl className="d-kv">
            <dt>Type</dt><dd>{main.type}</dd>
            {main.destination && <><dt>Launch from</dt><dd>The Director: {main.destination}{main.place && main.place !== main.destination ? ` (${main.place})` : ''}</dd></>}
            {variants.length > 0 && <><dt>Recommended power</dt><dd>{variants.join('  /  ')}{variants.length > 1 ? '   (normal to hardest version)' : ''}</dd></>}
            <dt>Mode</dt><dd>{main.pvp ? 'PvP' : 'PvE'}</dd>
          </dl>
          {raid && (
            <SmallButton onClick={() => push({ name: 'guide', title: main.name.replace(/:\s*(master|normal|legend|expert|grandmaster|contest|standard|prestige).*$/i, '').trim() })}>
              How to do it: mechanics guide and video
            </SmallButton>
          )}
          <p className="d-note">Details from Bungie's activity data. Encounter-by-encounter drops are not published by Bungie, so the loot pool below is for the whole activity.</p>
        </>
      ) : (
        <p className="d-note big">Bungie's item data names this source, but has no separate activity entry for it (it can be a vendor, a playlist or a reward track). Everything that comes from it is listed below.</p>
      )}
      <LootGroups list={ix.items.filter((i) => i.source === source)} push={push} />
    </>
  );
}

// ---------------- item search ----------------


// the armour slot from the item's type ("Titan Mark", "Hunter Cloak" and "Warlock Bond" are the class items)
const slotOf = (i: D2Item) => (i.itemType !== 2 ? '' : /Mark|Cloak|Bond/.test(i.type) ? 'Class Item' : i.type.replace(/ Armor$/, ''));
const ARMOUR_SLOTS = ['Helmet', 'Gauntlets', 'Chest', 'Leg', 'Class Item'];
const SORTS_D = [['match', 'Best match'], ['name', 'Name'], ['rarity', 'Rarity'], ['type', 'Type'], ['newest', 'Newest']] as const;
type SortD = (typeof SORTS_D)[number][0];
const PAGE = 120;

/** Which item names have a community PvE / PvP god roll (any version of the name: the list covers current copies). */
let rollNames: Promise<{ pve: Set<string>; pvp: Set<string> }> | undefined;
function useRollNames(ix: D2Index) {
  const [v, setV] = useState<{ pve: Set<string>; pvp: Set<string> }>();
  useEffect(() => {
    let live = true;
    rollNames ??= godRolls().then((t) => {
      const pve = new Set<string>(), pvp = new Set<string>();
      for (const i of ix.items) for (const g of t[i.hash] ?? []) { if (g.mode !== 'pvp') pve.add(i.norm); if (g.mode !== 'pve') pvp.add(i.norm); }
      return { pve, pvp };
    });
    rollNames.then((x) => live && setV(x)).catch(() => undefined);
    return () => { live = false; };
  }, [ix]);
  return v;
}

/** The search's filter buttons, kept for the visit: within a row any button counts, across rows all must. */
let lastSearch: { q: string; on: string[]; sort: SortD } = { q: '', on: [], sort: 'match' };

/**
 * Item Search: a name, weapon type or activity, and rows of filter buttons (kind, rarity, weapon type, element, ammo,
 * class, armour slot, where it comes from, community god roll). With nothing typed or picked it starts on the exotics.
 */
function Search({ ix, push }: Ctx) {
  const [q, setQ] = useState(lastSearch.q);
  const [on, setOn] = useState<string[]>(lastSearch.on);
  const [sort, setSort] = useState<SortD>(lastSearch.sort);
  const [shown, setShown] = useState(PAGE);
  useEffect(() => { lastSearch = { q, on, sort }; setShown(PAGE); }, [q, on, sort]);
  const rolls = useRollNames(ix);
  const flip = (k: string) => setOn((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const has = (row: string) => on.some((k) => k.startsWith(`${row}:`));
  const weaponTypes = useMemo(() => [...new Set(ix.items.filter((i) => i.itemType === 3 && i.type).map((i) => i.type))].sort(), [ix]);
  const showWeapon = !has('kind') || on.includes('kind:weapon'), showArmour = !has('kind') || on.includes('kind:armour');

  const list = useMemo(() => {
    const n = norm(q);
    const words = q.toLowerCase().split(/\s+/).filter(Boolean).map(norm).filter(Boolean);
    const rows = new Map<string, string[]>();
    for (const k of on) { const [r, v] = k.split(':'); rows.set(r, [...(rows.get(r) ?? []), v]); }
    const test: Record<string, (i: D2Item, v: string) => boolean> = {
      kind: (i, v) => (v === 'weapon' ? i.itemType === 3 : i.itemType === 2),
      tier: (i, v) => i.tier === Number(v),
      wtype: (i, v) => i.itemType === 3 && i.type === v,
      el: (i, v) => i.itemType === 3 && i.damage === Number(v),
      ammo: (i, v) => i.itemType === 3 && i.ammo === Number(v),
      cls: (i, v) => i.itemType === 2 && i.classType === Number(v),
      slot: (i, v) => slotOf(i) === v,
      from: (i, v) => i.category === v,
      roll: (i, v) => i.itemType === 3 && !!rolls && (v === 'pve' ? rolls.pve : rolls.pvp).has(i.norm),
    };
    let l = ix.items.filter((i) =>
      [...rows].every(([r, vs]) => vs.some((v) => test[r]?.(i, v) ?? true)) &&
      (n.length === 0 || words.every((w) => i.norm.includes(w) || i.normType.includes(w) || i.normSrc.includes(w) || norm(ELEMENTS[i.damage] ?? '') === w)));
    if (!n && !on.length) l = l.filter((i) => i.tier === 6);
    const by: Record<SortD, (a: D2Item, b: D2Item) => number> = {
      // exact names at the top, then the rarest, the newest copy of a name first
      match: (a, b) => Number(b.norm === n) - Number(a.norm === n) || b.tier - a.tier || a.name.localeCompare(b.name) || b.hash - a.hash,
      name: (a, b) => a.name.localeCompare(b.name) || b.hash - a.hash,
      rarity: (a, b) => b.tier - a.tier || a.name.localeCompare(b.name),
      type: (a, b) => a.type.localeCompare(b.type) || b.tier - a.tier || a.name.localeCompare(b.name),
      newest: (a, b) => b.hash - a.hash,
    };
    return l.sort(by[sort]);
  }, [ix, q, on, sort, rolls]);

  const row = (title: string, keys: [string, ReactNode, string?][], cls = '') => (
    <div className={`d-frow ${cls}`}>
      <span className="d-frow-t">{title}</span>
      <div className="d-frow-b">{keys.map(([k, label, tip]) => (
        <button key={k} type="button" className={`d-fbtn ${on.includes(k) ? 'on' : ''}`} aria-pressed={on.includes(k)} title={tip} onClick={() => flip(k)}>{label}</button>
      ))}</div>
    </div>
  );
  const dot = (c: string) => <i className="d-fdot" style={{ background: c }} />;
  return (
    <>
      <div className="d-searchrow">
        <input autoFocus className="d-search" placeholder="Search a name, weapon type or activity (e.g. 'pulse rifle trials', 'Fatebringer')" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="d-sort">Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortD)}>{SORTS_D.map(([k, label]) => <option key={k} value={k}>{label}</option>)}</select>
        </label>
      </div>
      <div className="d-filters">
        {row('Kind', [['kind:weapon', 'Weapons'], ['kind:armour', 'Armour']])}
        {row('Rarity', [['tier:6', <>{dot('#CEAE33')}Exotic</>], ['tier:5', <>{dot('#7A4FB0')}Legendary</>], ['tier:4', <>{dot('#5076A3')}Rare</>]])}
        {showWeapon && row('Element', Object.entries(ELEMENTS).map(([k, name]) => [`el:${k}`, <>{dot(DAMAGE_COLORS[Number(k)])}{name}</>]))}
        {showWeapon && row('Ammo', Object.entries(AMMO).map(([k, name]) => [`ammo:${k}`, name]))}
        {showWeapon && row('God roll', [['roll:pve', <>{dot('#5fd39a')}PvE god roll</>, 'Weapons with a community PvE god roll (DIM wish list)'], ['roll:pvp', <>{dot('#f0707a')}PvP god roll</>, 'Weapons with a community PvP god roll (DIM wish list)']])}
        {showWeapon && row('Weapon type', weaponTypes.map((t) => [`wtype:${t}`, t]), 'wide')}
        {showArmour && row('Class', Object.entries(CLASS_NAMES).map(([k, name]) => [`cls:${k}`, name]))}
        {showArmour && row('Armour slot', ARMOUR_SLOTS.map((s) => [`slot:${s}`, s]))}
        {row('Where from', CATEGORIES.map(([name]) => [`from:${name}`, name]))}
      </div>
      <div className="d-resultbar">
        <span className="d-note">{q.trim() || on.length ? `${list.length.toLocaleString()} result${list.length === 1 ? '' : 's'}` : 'Type a name, weapon type or activity, or pick some filters. Showing the exotics to start.'}</span>
        {(q || on.length > 0) && <button type="button" className="g-small" onClick={() => { setQ(''); setOn([]); }}>Clear search and filters</button>}
      </div>
      <div className="d-tiles">{list.slice(0, shown).map((it) => <ItemCard key={it.hash} it={it} onClick={() => push({ name: 'item', item: it })} pve={it.itemType === 3 && rolls?.pve.has(it.norm)} pvp={it.itemType === 3 && rolls?.pvp.has(it.norm)} />)}</div>
      {list.length > shown && (
        <div className="d-more"><button type="button" className="g-small" onClick={() => setShown(shown + PAGE)}>Show {Math.min(PAGE, list.length - shown)} more ({(list.length - shown).toLocaleString()} left)</button></div>
      )}
    </>
  );
}

// ---------------- one item ----------------

/**
 * View 3D for one weapon or armour piece (port of the app's OpenItem3DAsync): built with the same builder and viewer as
 * the 3D view, in a panel beside the item, on the item's own inspect picture (a grey studio without one).
 */
const itemScenes = new Map<number, Scene>();
// the showcase's light in the item's rarity: exotic gold, legendary purple, rare blue, uncommon green
const RARITY_GLOW: Record<number, string> = { 6: 'e2b93b', 5: '8a5cf0', 4: '4f8fd6', 3: '4fae6a' };
function Item3D({ m, close }: { m: ItemDetail; close: () => void }) {
  const [scene, setScene] = useState<Scene | undefined>(itemScenes.get(m.hash));
  const [msg, setMsg] = useState(`Building ${m.name} in 3D...`);
  const panel = useRef<HTMLDivElement>(null), stage = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  // on a narrow screen the panel opens above the item, out of sight: bring it into view
  useEffect(() => { if (matchMedia('(max-width: 1150px)').matches) panel.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === stage.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  useEffect(() => {
    if (scene) return;
    let live = true;
    buildScene([{ hash: m.hash, name: m.name, bucket: 0, shaderName: '', ornamentName: '', weapon: m.itemType === 3 }], false, (s) => { if (live) setMsg(s); })
      .then((sc) => { itemScenes.set(m.hash, sc); if (live) setScene(sc); })
      .catch(() => { if (live) setMsg(`Bungie has no usable 3D model for ${m.name}.`); });
    return () => { live = false; };
  }, [m.hash]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (scene) (window as any).__d2cScene = scene.files;   // the viewer page (same site) reads the package from here
  const photo = m.screenshot ? `&photo=${encodeURIComponent(sameOriginImg(m.screenshot)!)}` : '';
  return (
    <div className="d-3d" ref={panel}>
      <div className="d-3d-head">
        <b>{m.name}  -  3D</b>
        <span className="d-3d-buttons">
          {document.fullscreenEnabled && <button type="button" className="g-small" onClick={() => (document.fullscreenElement ? void document.exitFullscreen() : void stage.current?.requestFullscreen())}>{full ? 'Exit full screen' : 'Full screen'}</button>}
          <button type="button" className="g-small" onClick={close}>Close</button>
        </span>
      </div>
      <div className="d-3d-stage" ref={stage}>
        {scene ? <iframe title={`${m.name} in 3D`} src={`/viewer3d.html?scene=mem&show=1&tint=%23${RARITY_GLOW[m.tierType] ?? '9aa6bf'}&bg=%23181a20,%230f1014,%23060709${photo}&dark=1&t=item${m.hash}`} />
          : <p>{msg}</p>}
      </div>
      <p className="d-note">{touch ? 'One finger turns it any way, two fingers move and pinch-zoom it, two taps reset.'
        : 'Drag to turn it any way, right-drag (or Shift-drag) to move it, wheel to zoom, double-click to reset.'}</p>
    </div>
  );
}

const MODE_NAMES = { pve: 'PvE', pvp: 'PvP', any: 'PvE or PvP' } as const;
const MODE_WHAT = { pve: 'raids, dungeons, Nightfalls and the like', pvp: 'the Crucible, Trials and Iron Banner', any: 'picks not marked for one or the other' } as const;

/** How many of a mode's community rolls name each perk (a perk counts once per roll); "either" rolls count for both. */
function tally(rolls: GodRoll[], mode: GodRoll['mode']) {
  const t = new Map<string, number>();
  for (const r of rolls) if (r.mode === mode || (mode !== 'any' && r.mode === 'any')) for (const n of new Set(r.cols.flat().map((p) => p.name))) t.set(n, (t.get(n) ?? 0) + 1);
  return t;
}

/**
 * One mode's god roll: first, for each of the weapon's perk columns, the perks the community picks for it, most
 * picked first (the top one lit), so PvE and PvP can be told apart at a glance; then the community rolls themselves
 * with their notes, folded away.
 */
function ModeRolls({ mode, rolls, columns }: { mode: GodRoll['mode']; rolls: GodRoll[]; columns: PerkColumn[] }) {
  const [all, setAll] = useState(false);
  const own = rolls.filter((r) => r.mode === mode);
  const t = tally(rolls, mode);
  // how many rolls the counts are out of (a roll for either mode counts in both)
  const of = mode === 'any' ? own.length : rolls.filter((r) => r.mode === mode || r.mode === 'any').length;
  const picks = columns.filter((c) => !c.intrinsic).map((c, k) => ({
    key: k,
    label: c.perks[0].type,
    perks: c.perks.filter((p) => !p.enhanced && t.has(p.name)).sort((a, b) => t.get(b.name)! - t.get(a.name)!).slice(0, 3),
  })).filter((c) => c.perks.length);
  return (
    <div className={`d-roll ${mode}`}>
      <div className="d-roll-head"><b>{MODE_NAMES[mode]}</b><span>{own.length ? `${own.length} community roll${own.length === 1 ? '' : 's'}, for ${MODE_WHAT[mode]}` : `for ${MODE_WHAT[mode]}`}</span></div>
      {!own.length && <p className="d-note">No {MODE_NAMES[mode]} roll is listed for this weapon.</p>}
      {own.length > 0 && picks.length > 0 && (
        <div className="d-picks">
          {picks.map((c) => (
            <div key={c.key} className="d-pickrow">
              <span className="t">{c.label}</span>
              <span className="d-pickperks">
                {c.perks.map((p, i) => (
                  <span key={p.hash} className={`d-pick ${i === 0 ? 'top' : ''}`} title={`${p.name}: in ${t.get(p.name)} of the ${of} ${MODE_NAMES[mode]} rolls\n\n${p.desc}`}>
                    {p.icon && <Pic src={p.icon} width={22} height={22} />}<span>{p.name}</span>{of > 1 && <small>{t.get(p.name)}/{of}</small>}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
      {own.length > 0 && picks.length > 0 && (
        <button type="button" className="g-small" onClick={() => setAll(!all)}>{all ? 'Hide the community rolls' : `Show the ${own.length} community roll${own.length === 1 ? '' : 's'} and notes`}</button>
      )}
      {(all || !picks.length) && own.map((r, i) => (
        <div key={i} className="d-rollgroup">
          <div className="d-rcols">
            {r.cols.map((c, k) => <div key={k} className="d-rcol">{c.map((p) => <PerkChip key={p.hash} p={p} />)}</div>)}
          </div>
          {r.note && <p className="d-note">{r.note.replace(/\s+/g, ' ').trim()}{r.note.length >= 300 ? '...' : ''}</p>}
        </div>
      ))}
    </div>
  );
}

function ItemPage({ ix, push, it }: Ctx & { it: D2Item }) {
  const [m, setM] = useState<ItemDetail>();
  const [three, setThree] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    loadItem(ix, it).then((d) => live && setM(d)).catch((e) => live && setError(String(e?.message ?? e)));
    return () => { live = false; };
  }, [ix, it]);
  // the tab's title names the item, as the worker's page HTML does for ?item= links
  useEffect(() => { document.title = `${it.name}: ${it.tierName ? `${it.tierName} ` : ''}${it.type || 'item'}, perks and god rolls | D2 Companion`; }, [it]);
  if (error) return <Asking text={`This item could not be loaded: ${error}`} />;
  if (!m) return <Asking text={`Loading ${it.name}...`} />;

  // the wish list's groups for this weapon (or a same-named copy), by what they are for; a perk is tagged PvE and / or
  // PvP below when a group for that mode allows it (by name, so the enhanced version counts too; a group for either
  // counts for both)
  const rolls = m.rolls.length ? m.rolls : m.altRolls;
  const inMode = (mode: 'pve' | 'pvp') => new Set(rolls.filter((r) => r.mode === mode || r.mode === 'any').flatMap((r) => r.cols.flat().map((p) => p.name)));
  const pveSet = inMode('pve'), pvpSet = inMode('pvp');
  let modes: GodRoll['mode'][] | null = null;
  let rollNote = '';
  if (m.itemType === 3 && m.tierType !== 6) {
    if (rolls.length) {
      modes = (['pve', 'pvp', 'any'] as const).filter((mode) => mode !== 'any' || rolls.some((r) => r.mode === 'any'));
      if (!m.rolls.length) rollNote = 'This exact version has no listed roll (usually an older, retired copy). These are the community god rolls for the current version of the same weapon; its perks are tagged below.';
    } else if (m.fixed) rollNote = 'This version has no random rolls: every copy comes with the perks below (where a column has more than one, you can switch between them), so there is no god roll to chase.';
    else rollNote = 'No community god roll is listed for this weapon (DIM wish list), and no other version of it has one either.';
  }
  const line = [m.tier, m.type];
  if (m.itemType === 3) { if (ELEMENTS[m.damage]) line.push(ELEMENTS[m.damage]); if (AMMO[m.ammo]) line.push(`${AMMO[m.ammo]} ammo`); }
  if (m.itemType === 2 && CLASS_NAMES[m.classType]) line.push(CLASS_NAMES[m.classType]);

  return (
    <div className={`d-itemlayout ${three ? 'with3d' : ''}`}>
      {three && <Item3D key={m.hash} m={m} close={() => setThree(false)} />}
      <div className="d-left">
        <div className="d-shot" style={m.screenshot ? { backgroundImage: `url(${img(m.screenshot)})` } : undefined}>
          <div className="shade" />
          <div className="words">
            <h2>{m.name.toUpperCase()}</h2>
            <div>{line.filter(Boolean).join('   |   ')}</div>
            <div className="d-srcrow">
              <button type="button" className={`d-source ${m.source ? 'link' : ''}`} disabled={!m.source} onClick={() => m.source && push({ name: 'activity', source: m.source })}
                title={m.source ? 'Show this activity: what it is, where it launches from, and everything that drops there' : undefined}>
                <span>Where to get it:</span> <b>{m.source || 'not listed by Bungie (not a Collections item)'}</b>{m.source && <span className="arrow">›</span>}
              </button>
              {(m.itemType === 3 || m.itemType === 2) && (
                <button type="button" className={`d-view3d ${three ? 'on' : ''}`} onClick={() => setThree(true)} title="Opens a 3D model of this item, drag to turn it (Bungie's 3D gear data is read the first time)">
                  <Art d={Draw.Cube} size={20} stroke={1.6} /><span>View in 3D</span>
                </button>
              )}
            </div>
          </div>
          {m.flavor && <div className="flavor">{m.flavor}</div>}
        </div>
        {m.stats.length > 0 && (
          <div className="d-panel">
            <Head>Stats</Head>
            <div className="d-stats">
              {m.stats.map((s) => (
                <div key={s.name} className="d-stat">
                  <span>{s.name}</span>
                  <span className="bar">{s.value <= 100 && <Bar fraction={s.value / 100} height={6} />}</span>
                  <b>{s.value}</b>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="d-side">
        {m.itemType === 3 && (
          <div className="d-panel">
            <div className="d-godroll">★ GOD ROLL</div>
            {m.tierType === 6 && <p className="d-gold">Exotic weapons have fixed perks: every copy is the same, so there is no god roll to chase. Its intrinsic and exotic perks are listed below.</p>}
            {rollNote && <p className={modes ? 'd-gold' : 'd-note'}>{rollNote}</p>}
            {modes && <div className="d-rolls">{modes.map((mode) => <ModeRolls key={mode} mode={mode} rolls={rolls} columns={m.columns} />)}</div>}
          </div>
        )}
        {m.columns.length > 0 && (
          <div className="d-panel">
            <Head>{m.itemType === 3 && rolls.length ? 'Perks   ( PvE / PvP  =  in the community god rolls )' : 'Perks'}</Head>
            <div className="d-cols">
              {m.columns.map((c, i) => (
                <div key={i} className="d-col">
                  <span className="t">{c.intrinsic ? 'INTRINSIC' : c.perks[0].type.toUpperCase()}</span>
                  {c.perks.map((p) => <PerkChip key={p.hash} p={p} pve={pveSet.has(p.name)} pvp={pvpSet.has(p.name)} />)}
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="d-note">God rolls: the DIM community wish list (48klocs/dim-wish-list-sources). Perks and stats: Bungie's item data.</p>
      </div>
    </div>
  );
}

// ---------------- raid / dungeon guide ----------------

function GuidePage({ title }: { title: string }) {
  const [g, setG] = useState<Guide>();
  const [error, setError] = useState('');
  useEffect(() => { guide(title).then(setG).catch((e) => setError(String(e?.message ?? e))); }, [title]);
  const yt = `https://www.youtube.com/results?search_query=${encodeURIComponent(`${title} destiny 2 guide`)}`;
  return (
    <div className="d-guide">
      <div className="d-guidebar">
        <a className="g-small" href={yt} target="_blank" rel="noreferrer">Video guides on YouTube</a>
        {g && <a className="g-small" href={g.url} target="_blank" rel="noreferrer">Open the full article</a>}
      </div>
      {error ? <p className="d-bad">{error}</p> : !g ? <Asking text="Loading the mechanics guide from the Destiny Wiki..." /> : (
        <>
          <p className="d-note">From the Destiny Wiki article "{g.title}" (destiny.fandom.com, CC BY-SA).</p>
          {g.blocks.map((b, i) => <div key={i} className={`d-g-${b.kind}`}>{b.kind === 'li' ? `-  ${b.text}` : b.kind === 'h2' ? b.text.toUpperCase() : b.text}</div>)}
        </>
      )}
    </div>
  );
}
